import { faStore, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React, { useEffect, useMemo, useState } from 'react';
import { View, Image, Pressable, Modal, useWindowDimensions } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { DeviceTheme, GetMissionsFromMapDto, MissionsClient } from '../../../api/api.g';
import useCustomTheme from '../../../hooks/theme-hook';
import useTranslation from '../../../hooks/translation-hook';
import Label from '../Label';
import { useQuery } from '@tanstack/react-query';
import useApi from '../../../hooks/api-hook';
import { MapLocation } from '../../../models/MapModels';
import { useAppSelector } from '../../../hooks/store-hook';
import Constants from 'expo-constants';
import Loading from '../Loading';
import { getForegroundLocationWithoutPrompt } from '../../../helpers/locationPermission';
import Overlay from '../Overlay';
import Popup from '../Popup';
import { readMissions } from '../../../helpers/missionRead';

export interface OnARollPopupProps {
  onClose?(): void;
  onMissionSelect?(missions: GetMissionsFromMapDto): void;
}

const OnARollPopup = (props: OnARollPopupProps) => {
  const t = useTranslation();
  const theme = useCustomTheme();
  const layout = useWindowDimensions();
  const apiMissions = useApi(MissionsClient);
  const authStore = useAppSelector((selector) => selector.auth);
  const userPrefs = useAppSelector((selector) => selector.userPrefs);
  const isDarkMode = userPrefs.theme === DeviceTheme.Dark;
  const defaultPosition: MapLocation = Constants.expoConfig.extra.defautlLocation;
  const [currentLocation, setCurrentLocation] = useState<MapLocation | undefined>(undefined);
  const [locationReady, setLocationReady] = useState(false);
  const [showPopup, setShowPopup] = useState(true);
  const popupBackgroundColor = isDarkMode ? theme.background.modal.toString() : '#fef1dd';
  const popupTitleColor = isDarkMode ? theme.primary.toString() : '#f5a523';
  const popupDescriptionColor = isDarkMode ? theme.text.primary.toString() : '#8e183d';
  const popupSeparatorColor = isDarkMode ? theme.borderColorLight.toString() : 'rgba(245, 165, 35, 0.2)';
  const popupItemSubtitleColor = isDarkMode ? theme.text.darkTurquoiseTwo.toString() : '#035164';
  const popupCloseColor = isDarkMode ? theme.text.primary.toString() : theme.background.primary.toString();
  const popupWidth = Math.min(350, layout.width - 24);
  const popupHeight = Math.min(560, layout.height - 40);
  const heroHeight = Math.round((popupWidth * 234) / 350);
  const hasCurrentLocation = Number.isFinite(currentLocation?.lat) && Number.isFinite(currentLocation?.lng);

  const defaultLogo = useMemo(
    () => (
      <FontAwesomeIcon
        icon={faStore}
        style={{
          padding: 7,
          color: theme.text.titleLight.toString(),
          width: '100%',
          height: '100%',
        }}
      />
    ),
    [theme]
  );

  useEffect(() => {
    let mounted = true;

    getForegroundLocationWithoutPrompt(defaultPosition)
      .then((location) => {
        if (mounted) {
          setCurrentLocation(location);
        }
      })
      .finally(() => {
        if (mounted) {
          setLocationReady(true);
        }
      });

    return () => {
      mounted = false;
    };
  }, [defaultPosition.lat, defaultPosition.lng]);

  const getLogo = (logoUrl) => {
    if (logoUrl) {
      return <Image style={{ width: '100%', height: '100%' }} source={{ uri: logoUrl }} />;
    } else {
      return defaultLogo;
    }
  };

  const handleOnClosePress = () => {
    setShowPopup(false);
    props.onClose && props.onClose();
  };

  const getBoundingBox = (centerLat, centerLon, distanceKm) => {
    const latDiff = distanceKm / 110.574; // Approximate degrees of latitude per kilometer
    const lonDiff = distanceKm / (111.32 * Math.cos((centerLat * Math.PI) / 180)); // Approximate degrees of longitude per kilometer

    const bottomRight = {
      lat: centerLat - latDiff,
      lng: centerLon + lonDiff,
    };

    const topLeft = {
      lat: centerLat + latDiff,
      lng: centerLon - lonDiff,
    };

    return { bottomRight, topLeft };
  };

  const mapQuery = useQuery({
    enabled: locationReady && hasCurrentLocation,
    useErrorBoundary: false,
    networkMode: 'always',
    retry: false,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    queryKey: ['missionSuggestions', authStore.user?.uid, currentLocation?.lat, currentLocation?.lng, userPrefs.uiLanguage],
    queryFn: ({ signal }) => {
      const boundingBox = getBoundingBox(currentLocation?.lat, currentLocation?.lng, 50);

      return readMissions((token) => apiMissions.getMissionsFromMap(
        [],
        `${currentLocation.lat}&${currentLocation.lng}`,
        `${boundingBox.bottomRight.lat}&${boundingBox.bottomRight.lng}`,
        `${boundingBox.topLeft.lat}&${boundingBox.topLeft.lng}`,
        true,
        token,
      ), signal);
    },
  });
  const isLoading = !locationReady || (hasCurrentLocation && (mapQuery.isFetching || mapQuery.isInitialLoading));

  const missions = [...(mapQuery.data?.highOrderMissions ?? []), ...(mapQuery.data?.missions ?? [])].slice(0, 4);
  const hasError = mapQuery.isError || (locationReady && !hasCurrentLocation);
  const loadError = t('missions-load-error');

  return (
    <Modal animationType="none" visible={showPopup} transparent={true} onRequestClose={handleOnClosePress}>
      <Overlay showOverLay={true} zIndex={3000} onPress={handleOnClosePress}>
        <Popup scrollable={false}>
          <View
            style={{
              backgroundColor: popupBackgroundColor,
              width: popupWidth,
              height: popupHeight,
              maxWidth: popupWidth,
              maxHeight: popupHeight,
              overflow: 'hidden' as any,
            }}
          >
            <Image source={require('../../../../assets/on-a-roll.png')} style={{ width: popupWidth, height: heroHeight }} resizeMode="cover" />
            <Pressable style={{ position: 'absolute' as any, top: 20, right: 20 }} onPress={handleOnClosePress}>
              <FontAwesomeIcon icon={faXmark} size={30} color={popupCloseColor} />
            </Pressable>
            <View
              style={{
                flex: 1,
                marginTop: 8,
                paddingLeft: 12,
                paddingRight: 12,
                paddingBottom: 22,
                backgroundColor: popupBackgroundColor,
              }}
            >
              <Label style={{ fontSize: 28, color: popupTitleColor, fontWeight: '600', width: '100%', flexShrink: 0 }} numberOfLines={3}>
                {t('on-a-roll-title')}
              </Label>
              <Label style={{ marginTop: 12, fontSize: 18, color: popupDescriptionColor, fontWeight: '400', width: '100%', flexShrink: 0 }} numberOfLines={10}>
                {t('on-a-roll-desc')}
              </Label>
              {!isLoading ? (
                <ScrollView
                  style={{ flex: 1, marginTop: 12 }}
                  contentContainerStyle={{ paddingBottom: 8 }}
                  nestedScrollEnabled={true}
                  bounces={false}
                  alwaysBounceVertical={false}
                  alwaysBounceHorizontal={false}
                  overScrollMode="never"
                >
                  {(hasError || missions.length === 0) && (
                    <View style={{ gap: 12 }}>
                      <Label>{hasError ? loadError : t('no-nearby-missions-popup-title')}</Label>
                      <Pressable accessibilityRole="button" onPress={() => mapQuery.refetch()}>
                        <Label>{t('retry')}</Label>
                      </Pressable>
                      <Pressable accessibilityRole="button" onPress={handleOnClosePress}>
                        <Label>{t('back')}</Label>
                      </Pressable>
                    </View>
                  )}
                  {missions.map((x, i) => (
                    <Pressable
                      key={x.branchId}
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: 8,
                        borderTopWidth: i == 0 ? 1 : 0,
                        borderBottomWidth: 1,
                        borderColor: popupSeparatorColor,
                        paddingHorizontal: 20,
                        paddingVertical: 8,
                        alignItems: 'center',
                      }}
                      onPress={() => {
                        setShowPopup(false);
                        props.onMissionSelect && props.onMissionSelect(x);
                      }}
                    >
                      <View
                        style={{
                          display: 'flex' as any,
                          flexDirection: 'row' as any,
                          alignItems: 'center' as any,
                          justifyContent: 'center' as any,
                          width: 38,
                          height: 38,
                          borderRadius: 19,
                          borderWidth: 1,
                          borderColor: popupSeparatorColor,
                          overflow: 'hidden' as any,
                          backgroundColor: theme.background.secondary,
                        }}
                      >
                        {getLogo(x.logoUrl)}
                      </View>
                      <View style={{ gap: 5, flexShrink: 1 }}>
                        <Label style={{ fontSize: 14, fontWeight: '600', textTransform: 'uppercase', color: theme.text.primary.toString(), flexShrink: 1 }} numberOfLines={2}>
                          {x.missionConfigurationNames?.[0] ?? x.name}
                        </Label>
                        <Label style={{ fontSize: 11, color: popupItemSubtitleColor, fontWeight: '600', flexShrink: 1 }} numberOfLines={2}>
                          {x.name}
                        </Label>
                      </View>
                    </Pressable>
                  ))}
                </ScrollView>
              ) : (
                <View
                  style={{
                    flex: 1,
                    minHeight: 0,
                    marginTop: 12,
                    justifyContent: 'center',
                    backgroundColor: popupBackgroundColor,
                  }}
                >
                  <Loading style={{ backgroundColor: 'transparent' }} iconColor={isDarkMode ? '#ffffff' : undefined} />
                </View>
              )}
            </View>
          </View>
        </Popup>
      </Overlay>
    </Modal>
  );
};

export default OnARollPopup;
